/** A customer administrator's forgotten password: a code to their own address, then a new password. It opens no session, so they sign in as usual afterwards. */
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, errorMessage } from '../lib/api'
import {
  PASSWORD_FORGOT_PATH,
  PASSWORD_RESET_PATH,
  passwordChallenge,
  passwordProblem,
  passwordText,
  refusalText,
  signInFailure,
} from '../lib/password'
import PasswordChecklist from './PasswordChecklist'
import { PasswordInput } from './PasswordInput'
import { btnPrimary, inputClass, labelClass, Notice } from './ui'

const quietButton =
  'w-full text-center text-xs text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300'

export default function ForgotPassword({
  initialHandle,
  onBack,
  onReset,
}: {
  initialHandle: string
  onBack: () => void
  onReset: () => void
}) {
  const { t } = useTranslation()
  const [handle, setHandle] = useState(initialHandle)
  const [challenge, setChallenge] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const ask = useMutation({
    mutationFn: async () => (await api.post(PASSWORD_FORGOT_PATH, { handle: handle.trim() })).data,
    onSuccess: (data) => setChallenge(passwordChallenge(data)),
    onError: (error) => setFailure(errorMessage(error)),
  })

  const reset = useMutation({
    mutationFn: async () =>
      (await api.post(PASSWORD_RESET_PATH, { challenge, code, new_password: next })).data,
    onSuccess: onReset,
    onError: (error) => {
      const text = refusalText(error)
      if (signInFailure(error)) setFailure(t('otp.secondStepRefused'))
      else setFailure(text ? passwordText(t, text) : errorMessage(error))
    },
  })

  const context = { email: handle.includes('@') ? handle.trim() : null, username: handle.includes('@') ? null : handle.trim() }
  const problem = next || confirm ? passwordProblem(next, context.username, confirm, context) : null
  const canSave = Boolean(code) && passwordProblem(next, context.username, confirm, context) === null && !reset.isPending

  function startOver() {
    setChallenge(null)
    setCode('')
    setNext('')
    setConfirm('')
    setFailure(null)
  }

  if (!challenge) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          setFailure(null)
          ask.mutate()
        }}
        className="space-y-3"
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{t('otp.forgotIntro')}</p>
        <label className="block text-sm">
          <span className={labelClass}>{t('password.handleLabel')}</span>
          <input
            type="text"
            required
            autoFocus
            autoComplete="username"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            className={`w-full ${inputClass}`}
          />
        </label>
        <button type="submit" disabled={ask.isPending} className={`w-full ${btnPrimary}`}>
          {ask.isPending ? t('otp.sending') : t('otp.forgotSend')}
        </button>
        {failure && <Notice tone="danger">{failure}</Notice>}
        <button type="button" onClick={onBack} className={quietButton}>
          {t('otp.forgotBack')}
        </button>
      </form>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        setFailure(null)
        if (canSave) reset.mutate()
      }}
      className="space-y-3"
    >
      <p className="text-sm text-slate-600 dark:text-slate-300">{t('otp.forgotSent')}</p>
      <label className="block text-sm">
        <span className={labelClass}>{t('otp.codeLabel')}</span>
        <input
          type="text"
          required
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={`w-full ${inputClass}`}
        />
      </label>
      <PasswordInput
        label={t('password.newPassword')}
        required
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
      />
      <PasswordInput
        label={t('password.confirmPassword')}
        required
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      <PasswordChecklist password={next} context={context} />
      {problem === 'mismatch' && <Notice tone="warning">{passwordText(t, problem)}</Notice>}
      {failure && <Notice tone="danger">{failure}</Notice>}
      <button type="submit" disabled={!canSave} className={`w-full ${btnPrimary}`}>
        {reset.isPending ? t('otp.forgotSaving') : t('otp.forgotSave')}
      </button>
      <button type="button" onClick={startOver} className={quietButton}>
        {t('otp.startOver')}
      </button>
      <button type="button" onClick={onBack} className={quietButton}>
        {t('otp.forgotBack')}
      </button>
    </form>
  )
}
